import Component from "../../lib/Component.js";
import { getCurrentUser } from "../auth/Auth.js";
import { logError, onTransitionEnd } from "../helpers.js";
import JustWatchButton from "./JustWatchButton.js";
import WatchlistToggle, { determineWatchlistButtonState } from "./WatchlistToggle.js";

// Track expanded movies per page load to prevent duplicate GA4 events
const expandedMoviesThisSession = new Set();

/**
 * Send GA4 custom event for movie expand
 * @param {number} movieId
 * @param {string} movieTitle
 * @param {string} pageType
 */
const sendExpandEvent = (movieId, movieTitle, pageType) => {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "movie_expand",
      movie_id: movieId,
      movie_title: movieTitle,
      page_type: pageType,
    });
    if (process.env.NODE_ENV === "development") {
      console.log("[GTM DataLayer Test] Sent expand event to dataLayer:", {
        event: "movie_expand",
        movie_id: movieId,
        movie_title: movieTitle,
        page_type: pageType,
      });
    }
  } catch (error) {
    // Silently fail if dataLayer is not available
    if (process.env.NODE_ENV === "development") {
      console.warn("Failed to send expand event to dataLayer:", error);
    }
  }
};

/**
 * @param {HTMLElement} item
 * @param {{scrollOffset: ?number}} param1
 * @return {Component}
 */
const ExpandableMovie = (item, { scrollOffset }) => {
  if (!item || !item.closest(".item-list.movie-list")) return Component.noop();

  let escapeKeyHandler = null;

  /**
   * Close the expanded movie zone
   * @param {HTMLLIElement} expandZone - The expand zone element to close
   */
  const closeExpandZone = (expandZone) => {
    if (!expandZone) return;

    expandZone.style.height = `${expandZone.offsetHeight}px`;
    requestAnimationFrame(() => {
      expandZone.style.height = "0px";
      onTransitionEnd(expandZone, "height", 300).then(() => {
        expandZone.remove();
        item.classList.remove("expanded");
        item.setAttribute("aria-expanded", "false");

        // Remove Escape key handler when zone is closed
        if (escapeKeyHandler) {
          document.removeEventListener("keydown", escapeKeyHandler);
          escapeKeyHandler = null;
        }
      });
    });
  };

  /** @param {Event} event */
  const expandMovie = (event) => {
    /** @type {HTMLLIElement | null} */
    const clickedItem = item.closest("li");

    /** @type {HTMLUListElement | null} */
    const list = item.closest(".item-list.movie-list");

    // Guard against missing DOM elements
    if (!clickedItem || !list) {
      return true;
    }

    /** @type {HTMLLIElement[]} */
    let listItems;
    try {
      listItems = Array.from(list.querySelectorAll(":scope > li:not(.item-expand-zone)"));
    } catch {
      // Fallback for old browsers that don't support :scope selector (e.g., Samsung Smart TV Tizen 8.0)
      // Let the browser handle the link click normally
      return true;
    }

    // Guard against empty list
    if (listItems.length === 0) {
      return true;
    }

    event.preventDefault();

    const index = listItems.indexOf(clickedItem);
    const itemsInRow = Math.round(list.offsetWidth / clickedItem.offsetWidth);
    const rowIndex = Math.floor(index / itemsInRow);
    const nextRowLastItemIndex = (rowIndex + 1) * itemsInRow - 1;
    const lastItemInTheRow = listItems[Math.min(nextRowLastItemIndex, listItems.length - 1)];

    // Guard against invalid item calculation
    if (!lastItemInTheRow) {
      return true;
    }

    const movieUrl = item.getAttribute("href");
    const expandedUrl = movieUrl.replace("/movie/", "/movie/expanded/");

    const allExpandableItems = document.querySelectorAll(".expandable-movie");
    let previousExpandable = null;
    let nextExpandable = null;
    allExpandableItems.forEach((expandableItem, idx) => {
      if (expandableItem === item) {
        if (idx > 0) {
          previousExpandable = allExpandableItems[idx - 1];
        }
        if (idx < allExpandableItems.length - 1) {
          nextExpandable = allExpandableItems[idx + 1];
        }
      }
    });

    /** @type {HTMLLIElement | null} */
    let expandZone = null;

    // Check if there's already an expand zone after the last item in this row
    /** @type {HTMLLIElement | null} */
    const existingExpandZone = lastItemInTheRow.nextElementSibling;
    if (existingExpandZone && existingExpandZone.classList.contains("item-expand-zone")) {
      expandZone = existingExpandZone;
    } else {
      // Step 1: Immediately remove any existing expand zones to prevent layout shift
      const existingZones = document.querySelectorAll(".item-expand-zone");

      if (existingZones.length > 0) {
        existingZones.forEach((zone) => {
          const associatedItemUrl = zone.dataset.movieUrl;
          const associatedItem = document.querySelector(`.item-list.movie-list > li > a[href='${associatedItemUrl}']`);
          if (associatedItem) {
            associatedItem.classList.remove("expanded");
            associatedItem.setAttribute("aria-expanded", "false");
          }
          zone.remove();

          // Remove any existing Escape key handlers
          if (escapeKeyHandler) {
            document.removeEventListener("keydown", escapeKeyHandler);
            escapeKeyHandler = null;
          }
        });
      }
    }

    // Step 2: Scroll to the clicked item after old zones are removed
    // Use requestAnimationFrame to ensure DOM has updated
    requestAnimationFrame(() => {
      const itemRect = clickedItem.getBoundingClientRect();
      const itemMarginTop = parseFloat(window.getComputedStyle(clickedItem.closest("li")).marginTop) || 0;

      if (itemRect.top > 100 || itemRect.top < 0) {
        window.scrollTo({
          top: window.scrollY + itemRect.top - (scrollOffset || 0) - itemMarginTop,
          behavior: "smooth",
        });
      }

      // Step 3: Create and expand the new zone immediately for better INP
      requestAnimationFrame(() => {
        let endZoneStartHeight = 0;
        if (!expandZone) {
          expandZone = document.createElement("li");
          expandZone.className = "item-expand-zone";
          lastItemInTheRow.insertAdjacentElement("afterend", expandZone);
        } else {
          endZoneStartHeight = expandZone.offsetHeight;
          if (expandZone.dataset.movieUrl === movieUrl) {
            // Already expanded, so click goes to details page
            window.location.href = movieUrl;
            return;
          }
        }

        (async () => {
          item.classList.add("expanded");
          item.setAttribute("aria-expanded", "true");
          expandZone.dataset.movieUrl = movieUrl;
          expandZone.innerHTML = `<div class="loading-indicator"><i class='fas fa-circle-notch fa-spin'></i></div>`;
          expandZone.classList.add("loading");

          // Track expand event in GA4 (once per movie per page load)
          const movieId = parseInt(item.getAttribute("href").split("/").slice(-1)[0].split("-")[0], 10);
          if (!expandedMoviesThisSession.has(movieId)) {
            expandedMoviesThisSession.add(movieId);

            // Determine page type from body class or URL
            let pageType = "list";
            if (document.location.pathname === "/") {
              pageType = "home";
            } else if (document.location.pathname.startsWith("/kereso")) {
              pageType = "search";
            } else if (document.location.pathname.startsWith("/streaming")) {
              pageType = "streaming";
            } else if (document.location.pathname.startsWith("/filmek")) {
              pageType = "year";
            }

            // Get movie title from the link text or data attribute
            console.log(item);
            const movieYear = item.querySelector(".item-subtitle")?.textContent?.trim().split(" ")[0] || "";
            const movieTitle =
              item.querySelector(".item-title")?.textContent?.trim() || item.getAttribute("aria-label") || "";

            sendExpandEvent(movieId, `${movieTitle} (${movieYear})`, pageType);
          }

          try {
            const expandedResponse = await fetch(expandedUrl, {
              headers: {
                "X-Requested-With": "XMLHttpRequest",
              },
            });
            const expandedContent = await expandedResponse.text();
            expandZone.innerHTML = expandedContent;
            expandZone.classList.remove("loading");
          } catch (error) {
            if (process.env.NODE_ENV === "development") {
              console.error("Failed to load expanded movie content:", error);
              expandZone.innerHTML = `<div class="error-message">Failed to load expanded movie content: ${error.message}</div>`;
              expandZone.classList.remove("loading");
              return;
            }
            window.location.href = movieUrl;
            return;
          }

          // Add close button event listener
          const closeButton = expandZone.querySelector(".movie-expand-close");
          if (closeButton) {
            closeButton.addEventListener("click", () => {
              closeExpandZone(expandZone);
            });
          }

          // Add Escape key handler for accessibility
          escapeKeyHandler = (e) => {
            if (e.key === "Escape") {
              const currentExpandZone = document.querySelector(".item-expand-zone.expanded");
              if (currentExpandZone) {
                closeExpandZone(currentExpandZone);
              }
            }
          };
          document.addEventListener("keydown", escapeKeyHandler);

          const prevButton = expandZone.querySelector(".movie-expand-prev");
          if (prevButton) {
            if (previousExpandable) {
              prevButton.addEventListener("click", () => {
                previousExpandable.click();
              });
            } else {
              prevButton.disabled = true;
            }
          }

          const nextButton = expandZone.querySelector(".movie-expand-next");
          if (nextButton) {
            if (nextExpandable) {
              nextButton.addEventListener("click", () => {
                nextExpandable.click();
              });
            } else {
              nextButton.disabled = true;
            }
          }

          // Calculate the actual height needed based on content
          requestAnimationFrame(() => {
            // Add expanded class first to apply proper styling
            expandZone.classList.add("expanded");
            expandZone.style.height = "auto";
            const actualHeight = expandZone.scrollHeight;

            // Reset for animation
            expandZone.style.height = `${endZoneStartHeight}px`;

            // Use another frame to trigger the animation
            requestAnimationFrame(async () => {
              expandZone.style.height = `${actualHeight}px`;

              await onTransitionEnd(expandZone, "height", 300);

              const trailerThumbnail = expandZone.querySelector(".movie-trailer-thumbnail-link img");
              if (trailerThumbnail && trailerThumbnail.naturalWidth === 120) {
                trailerThumbnail.onload = () => {
                  if (trailerThumbnail.naturalWidth === 120) {
                    if (trailerThumbnail.src.includes("hqdefault.jpg")) {
                      trailerThumbnail.src = trailerThumbnail.src.replace("hqdefault.jpg", "mqdefault.jpg");
                    } else if (trailerThumbnail.src.includes("mqdefault.jpg")) {
                      trailerThumbnail.src = trailerThumbnail.src.replace("mqdefault.jpg", "default.jpg");
                    }
                  }
                };
                trailerThumbnail.src = trailerThumbnail.src.replace("sddefault.jpg", "hqdefault.jpg");
              }

              const watchlistButton = expandZone.querySelector(".add-to-watchlist-button");
              WatchlistToggle(watchlistButton).mount();
              determineWatchlistButtonState(watchlistButton, await getCurrentUser());

              const movieId = item.getAttribute("href").split("/").slice(-1)[0].split("-")[0];
              JustWatchButton({
                movieId,
                streamingButton: expandZone.querySelector(".button.streaming"),
              }).mount();
            });
          });

          // Reload AdSense ads in the new content
          const adElements = expandZone.querySelectorAll(".adsbygoogle");
          if (adElements.length > 0) {
            adElements.forEach(() => {
              (window.adsbygoogle = window.adsbygoogle || []).push({});
            });
          }
        })();
      }, 150); // Short delay to let scroll animation start
    });

    event.preventDefault();
    return false;
  };

  const guardedExpandMovie = (event) => {
    try {
      return expandMovie(event);
    } catch (error) {
      logError(error, {
        component: "ExpandableMovie",
        tags: { feature: "movie_expansion", action: "expand_movie" },
        itemHref: item.getAttribute("href"),
      });
      return true; // Let the link work normally on error
    }
  };

  return new Component({
    onMount: () => {
      if (!item.classList.contains("expandable-movie")) {
        item.classList.add("expandable-movie");
        item.setAttribute("aria-expanded", "false");
        item.addEventListener("click", guardedExpandMovie);
      }
    },
    onUnmount: () => {
      if (item.classList.contains("expandable-movie")) {
        item.classList.remove("expandable-movie");
        item.removeAttribute("aria-expanded");
        item.removeEventListener("click", guardedExpandMovie);

        // Clean up Escape key handler on unmount
        if (escapeKeyHandler) {
          document.removeEventListener("keydown", escapeKeyHandler);
          escapeKeyHandler = null;
        }
      }
    },
  });
};

export default ExpandableMovie;
